"use client";

import { c as _c } from "react/compiler-runtime";
import { Button, EllipsisTooltip, Spacer, Tooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import Link from "next/link";
import { cloneElement, isValidElement, type Key, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Can } from "@/components/Ability/Can";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { type NavGroup, useNavigationItems } from "./navigationItems/useNavigationItems";
import type { SidebarItem } from "./Sidebar.types";
interface GroupedSidebarProps {
  defaultSelectedKey?: string;
  isCompact?: boolean;
  className?: string;
  onSelect?: (key: string) => void;
}
export const GroupedSidebar = t0 => {
  const $ = _c(31);
  const {
    defaultSelectedKey,
    isCompact: t1,
    className,
    onSelect
  } = t0;
  const isCompact = t1 === undefined ? false : t1;
  const [selected, setSelected] = useState(defaultSelectedKey);
  const {
    navigationGroups
  } = useNavigationItems();
  const flags = useFeatureFlags();
  const {
    build: buildPath
  } = useOrgTeamPath();
  let t2;
  if ($[0] !== flags || $[1] !== navigationGroups) {
    let t3;
    if ($[3] !== flags) {
      t3 = group => ({
        ...group,
        items: group.items.filter(item => {
          if (!item.featureFlag) {
            return true;
          }
          return flags[item.featureFlag];
        })
      });
      $[3] = flags;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = navigationGroups.map(t3);
    $[0] = flags;
    $[1] = navigationGroups;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const filteredNavigationGroups = t2;
  let t3;
  let t4;
  if ($[5] !== defaultSelectedKey) {
    t3 = () => {
      setSelected(defaultSelectedKey);
    };
    t4 = [defaultSelectedKey];
    $[5] = defaultSelectedKey;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== onSelect) {
    t5 = key => {
      setSelected(key);
      onSelect?.(key);
    };
    $[8] = onSelect;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleItemClick = t5;
  const renderIcon = _temp;
  let t6;
  if ($[10] !== buildPath || $[11] !== handleItemClick || $[12] !== isCompact || $[13] !== selected) {
    t6 = item_0 => {
      const isSelected = selected === item_0.key;
      const getItemHref = () => {
        if (!item_0.href) {
          return;
        }
        return buildPath(item_0.href);
      };
      const itemHref = getItemHref();
      const isActionItem = !item_0.href && !!item_0.onPress;
      const handleClick = () => {
        if (isActionItem) {
          item_0.onPress?.();
          return;
        }
        handleItemClick(item_0.key);
      };
      const buttonElement = <Button {...isActionItem ? {} : {
        as: Link,
        href: itemHref
      }} color="default" isDisabled={item_0.isDisabled} isIconOnly={isCompact} fullWidth={!isCompact} className={cn("mx-auto bg-transparent transition-transform hover:bg-navy-700 hover:text-zinc-300!", isCompact ? "justify-center" : "justify-start", isSelected && "bg-navy-900 font-medium text-zinc-300!", !isSelected && "text-zinc-300!")} startContent={!isCompact && item_0.icon ? renderIcon(item_0.icon) : null} endContent={!isCompact && item_0.endContent ? item_0.endContent : null} onPress={handleClick} data-testid={`nav-item-${item_0.key}`} aria-label={item_0.title}>{isCompact ? item_0.icon && renderIcon(item_0.icon) : <EllipsisTooltip as="span" content={item_0.title}>{item_0.title}</EllipsisTooltip>}</Button>;
      const wrappedButtonElement = isCompact ? <Tooltip key={item_0.key} content={item_0.title} placement="right" color="foreground">{buttonElement}</Tooltip> : <div key={item_0.key}>{buttonElement}</div>;
      if (item_0.requirePermission) {
        return <Can key={item_0.key} action={item_0.requirePermission.action} resource={item_0.requirePermission.resource} scopeId={item_0.requirePermission.scopeId} fallback={null}>{wrappedButtonElement}</Can>;
      }
      return wrappedButtonElement;
    };
    $[10] = buildPath;
    $[11] = handleItemClick;
    $[12] = isCompact;
    $[13] = selected;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const renderItem = t6;
  let t7;
  if ($[15] !== isCompact || $[16] !== renderItem) {
    t7 = (group_0, index) => {
      const renderedItems = group_0.items.map(renderItem).filter(Boolean);
      if (renderedItems.length === 0) {
        return null;
      }
      const renderedGroupLabel = group_0.label === "System Administration" && isCompact ? "Admin" : group_0.label;
      const groupContent = <div key={`group-${index}`} className="flex flex-col"><div className={cn("mb-2 px-3 font-mono text-[10px] text-default-500 uppercase", !isCompact && "pl-0", isCompact && "max-w-full truncate text-center")}>{renderedGroupLabel}</div><div className="flex flex-col gap-1" data-testid="sidebar-group-items">{renderedItems}</div></div>;
      if (group_0.requirePermission) {
        return <Can key={`group-${index}`} action={group_0.requirePermission.action} resource={group_0.requirePermission.resource} scopeId={group_0.requirePermission.scopeId} fallback={null}>{groupContent}</Can>;
      }
      return groupContent;
    };
    $[15] = isCompact;
    $[16] = renderItem;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const renderGroup = t7;
  let t8;
  if ($[18] !== className) {
    t8 = cn("mb-6 flex flex-col gap-2", className);
    $[18] = className;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== filteredNavigationGroups || $[21] !== isCompact || $[22] !== renderGroup) {
    let t10;
    if ($[24] !== filteredNavigationGroups.length || $[25] !== isCompact || $[26] !== renderGroup) {
      t10 = (group_1, index_0) => {
        const renderedGroup = renderGroup(group_1, index_0);
        if (!renderedGroup) {
          return null;
        }
        return <div key={`nav-group-${group_1.label}`}>{renderedGroup}{index_0 < filteredNavigationGroups.length - 1 && !isCompact && <Spacer y={2} />}</div>;
      };
      $[24] = filteredNavigationGroups.length;
      $[25] = isCompact;
      $[26] = renderGroup;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    t9 = filteredNavigationGroups.map(t10).filter(Boolean);
    $[20] = filteredNavigationGroups;
    $[21] = isCompact;
    $[22] = renderGroup;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <nav className={t8}>{t9}</nav>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
function _temp(iconNode) {
  if (!iconNode) {
    return null;
  }
  if (!isValidElement(iconNode)) {
    return iconNode;
  }
  const element = iconNode as ReactElement<{
    className?: string;
    size?: number;
  }>;
  return cloneElement(element, {
    className: element.props?.className,
    size: element.props?.size || 20
  });
}